import React, { useState, useRef, useEffect } from "react";
import styles from "./DriversPage.module.css";
import { Driver } from "./types";
import { mockDrivers } from "./mockData";

export default function DriversPage() {
  const [drivers, setDrivers] = useState<Driver[]>(mockDrivers);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDrawer, setShowDrawer] = useState(false);
  const [viewingDriver, setViewingDriver] = useState<Driver | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const filteredDrivers = drivers.filter(driver => 
    driver.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    driver.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    driver.vendor.toLowerCase().includes(searchQuery.toLowerCase()) ||
    driver.phoneNumber.includes(searchQuery)
  );

  const handleView = (driver: Driver) => {
    setViewingDriver({ ...driver });
    setShowDrawer(true);
    setOpenMenuId(null);
  };

  const handleDelete = (id: string) => {
    if (window.confirm("Are you sure you want to delete this driver?")) {
      setDrivers(drivers.filter(d => d.id !== id));
      setOpenMenuId(null);
    }
  };

  const handleCloseDrawer = () => {
    setShowDrawer(false);
    setViewingDriver(null);
  };

  const toggleMenu = (id: string) => {
    setOpenMenuId(openMenuId === id ? null : id);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const renderStars = (rating: number) => {
    return (
      <div className={styles.stars}>
        {[1, 2, 3, 4, 5].map((star) => (
          <span key={star} className={star <= rating ? styles.starFilled : styles.starEmpty}>
            ★
          </span>
        ))}
      </div>
    );
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>Drivers</h1>
        </div>
        <div className={styles.headerRight}>
          <button className={styles.columnsBtn}>Columns</button>
          <button className={styles.resetBtn}>Reset</button>
          <button className={styles.createBtn}>+ Create</button>
        </div>
      </div>

      <div className={styles.searchBar}>
        <input
          type="text"
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={styles.searchInput}
        />
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>FIRST NAME</th>
              <th>LAST NAME</th>
              <th>VENDOR</th>
              <th>EXTERNAL ID</th>
              <th>PHONE NUMBER</th>
              <th>RATINGS</th>
              <th>STATE</th>
              <th>LICENSE EXPIRY</th>
              <th>LABELS</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredDrivers.map((driver) => (
              <tr key={driver.id}>
                <td>
                  <div className={styles.nameCell}>
                    <div className={styles.avatar}>{getInitials(driver.firstName, driver.lastName)}</div>
                    <span>{driver.firstName}</span>
                  </div>
                </td>
                <td>{driver.lastName}</td>
                <td>{driver.vendor}</td>
                <td className={styles.externalId}>{driver.externalId}</td>
                <td>{driver.phoneNumber}</td>
                <td>{renderStars(driver.ratings)}</td>
                <td>
                  <span className={`${styles.stateBadge} ${styles[driver.state.toLowerCase().replace('-', '')]}`}>
                    {driver.state}
                  </span>
                </td>
                <td>{driver.licenseExpiry || "-"}</td>
                <td>
                  <div className={styles.labelsList}>
                    {driver.labels.slice(0, 1).join(", ")}
                  </div>
                </td>
                <td>
                  <div className={styles.actionsCell}>
                    <button 
                      className={styles.menuBtn}
                      onClick={() => toggleMenu(driver.id)}
                    >
                      ...
                    </button>
                    {openMenuId === driver.id && (
                      <div ref={menuRef} className={styles.dropdown}>
                        <button 
                          className={styles.dropdownItem}
                          onClick={() => handleView(driver)}
                        >
                          View
                        </button>
                        <button 
                          className={styles.dropdownItem}
                          onClick={() => alert("Balance and transactions feature coming soon")}
                        >
                          Balance and transactions
                        </button>
                        <button 
                          className={`${styles.dropdownItem} ${styles.dropdownItemDanger}`}
                          onClick={() => handleDelete(driver.id)}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={styles.footer}>
        <span className={styles.resultsCount}>
          Showing {filteredDrivers.length} of {drivers.length} drivers
        </span>
      </div>

      {showDrawer && viewingDriver && (
        <>
          <div className={styles.drawerOverlay} onClick={handleCloseDrawer} />
          <div className={styles.drawer}>
            <div className={styles.drawerHeader}>
              <h2 className={styles.drawerTitle}>Edit Driver</h2>
              <button className={styles.closeBtn} onClick={handleCloseDrawer}>
                &times;
              </button>
            </div>

            <div className={styles.drawerContent}>
              <div className={styles.driverProfile}>
                <div className={styles.avatarLarge}>{getInitials(viewingDriver.firstName, viewingDriver.lastName)}</div>
                <button className={styles.addPhotoBtn}>+</button>
              </div>

              <button className={styles.startShiftBtn}>Start a shift now (8 hours)</button>

              <form className={styles.form}>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>First name *</label>
                    <input
                      type="text"
                      className={styles.input}
                      value={viewingDriver.firstName}
                      readOnly
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>Last name *</label>
                    <input
                      type="text"
                      className={styles.input}
                      value={viewingDriver.lastName}
                      readOnly
                    />
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Mobile phone number *</label>
                    <input
                      type="text"
                      className={styles.input}
                      value={viewingDriver.phoneNumber}
                      readOnly
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>Vendor</label>
                    <input
                      type="text"
                      className={styles.input}
                      value={viewingDriver.vendor}
                      readOnly
                    />
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>External ID *</label>
                    <input
                      type="text"
                      className={styles.input}
                      value={viewingDriver.externalId}
                      readOnly
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>Employee ID</label>
                    <input
                      type="text"
                      className={styles.input}
                      value={viewingDriver.employeeId || ""}
                      readOnly
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Stripe Connected Account Id</label>
                  <input
                    type="text"
                    className={styles.input}
                    value={viewingDriver.stripeConnectedAccountId || ""}
                    readOnly
                  />
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Address</label>
                    <input
                      type="text"
                      className={styles.input}
                      value={viewingDriver.address || ""}
                      readOnly
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>License Expiry</label>
                    <input
                      type="text"
                      className={styles.input}
                      value={viewingDriver.licenseExpiry || ""}
                      readOnly
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Driver Labels</label>
                  <div className={styles.labelTags}>
                    {viewingDriver.labels.map((label, index) => (
                      <span key={index} className={styles.labelTag}>
                        {label}
                        <button type="button" className={styles.labelRemove}>&times;</button>
                      </span>
                    ))}
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Associated Vehicles</label>
                  <div className={styles.helpText}>
                    No vehicles associated
                  </div>
                </div>
              </form>
            </div>

            <div className={styles.drawerFooter}>
              <button className={styles.cancelBtn} onClick={handleCloseDrawer}>
                Cancel
              </button>
              <button className={styles.saveBtn}>
                Apply Changes
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}